// RankManager: Settings sub-dialog where an operator defines the vertical
// order of kitchen titles. Uses native HTML5 drag-and-drop (no extra
// dependency). Every change is saved as a full replacement via
// PUT /api/rank-hierarchy so ordering, adding, removing, and renaming
// all flow through one write. The list is single-source-of-truth for the
// Roster row order and the Excel export sort.
//
// Design: True Glass V2 card, gold rim, drag handle on the left, section
// pill on the right. Sections are three fixed groups (Leadership, Line,
// Support) so the Excel export can color-band them.

import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { GripVertical, Plus, Trash2, RotateCcw, Layers } from "lucide-react";

const SECTIONS = [
  { key: "Leadership", label: "Leadership", tint: "rgba(212,175,55,0.18)" },
  { key: "Line",       label: "Line",       tint: "rgba(120,180,220,0.14)" },
  { key: "Support",    label: "Support",    tint: "rgba(190,150,120,0.14)" },
];

function SectionBadge({ section }) {
  const s = SECTIONS.find((x) => x.key === section) || SECTIONS[1];
  return (
    <span
      className="px-2 py-0.5 rounded-full text-[0.62rem] tracking-[0.14em] uppercase font-semibold"
      style={{ background: s.tint, border: "1px solid rgba(255,255,255,0.12)", color: "#faf3df" }}
    >
      {s.label}
    </span>
  );
}

export default function RankManager({ onSaved }) {
  const [entries, setEntries] = useState([]);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSection, setNewSection] = useState("Line");
  const dragIndexRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rank-hierarchy");
      setEntries(data.entries || []);
      setIsDefault(!!data.is_default);
    } catch (e) {
      toast.error("Could not load rank hierarchy.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put("/rank-hierarchy", {
        entries: next.map(({ title, section }) => ({ title, section })),
      });
      setEntries(data.entries || []);
      setIsDefault(false);
      onSaved?.(data.entries || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save rank hierarchy.");
      load();
    } finally {
      setSaving(false);
    }
  };

  const move = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= entries.length || to >= entries.length) return;
    const next = entries.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    persist(next);
  };

  const remove = (idx) => {
    if (entries.length <= 1) {
      toast.error("At least one rank is required.");
      return;
    }
    const next = entries.slice();
    next.splice(idx, 1);
    persist(next);
  };

  const rename = (idx, title) => {
    const next = entries.slice();
    next[idx] = { ...next[idx], title };
    setEntries(next);
  };

  const commitRename = (idx) => {
    const title = (entries[idx]?.title || "").trim();
    if (!title) {
      load();
      return;
    }
    persist(entries);
  };

  const changeSection = (idx, section) => {
    const next = entries.slice();
    next[idx] = { ...next[idx], section };
    persist(next);
  };

  const add = () => {
    const t = newTitle.trim();
    if (!t) return;
    if (entries.some((e) => e.title.toLowerCase() === t.toLowerCase())) {
      toast.error(`"${t}" is already in the list.`);
      return;
    }
    persist([...entries, { title: t, section: newSection }]);
    setNewTitle("");
  };

  const resetToDefault = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/rank-hierarchy/reset");
      setEntries(data.entries || []);
      setIsDefault(true);
      onSaved?.(data.entries || []);
      toast.success("Restored default kitchen hierarchy.");
    } catch (e) {
      toast.error("Could not reset.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="rank-manager">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Layers className="w-3 h-3" /> Kitchen Hierarchy
          </div>
          <h3 className="font-display text-lg mt-1">Rank Manager</h3>
          <p className="text-[0.72rem] text-muted-foreground mt-1 max-w-md leading-relaxed">
            Drag rows to reorder your kitchen ladder. The Roster grid and the industry Excel export both sort by this list, top down.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={resetToDefault}
          disabled={saving || isDefault}
          data-testid="rank-reset"
          title="Restore the nine-rank default"
        >
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset
        </Button>
      </div>

      {loading ? (
        <div className="rounded-xl glass-card p-6 text-center text-sm text-muted-foreground" data-testid="rank-loading">
          Loading hierarchy...
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.08)" }}>
          {entries.map((e, idx) => (
            <div
              key={`${e.title}-${idx}`}
              draggable
              onDragStart={(ev) => {
                dragIndexRef.current = idx;
                ev.dataTransfer.effectAllowed = "move";
                try { ev.dataTransfer.setData("text/plain", String(idx)); } catch {}
              }}
              onDragOver={(ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = "move"; }}
              onDrop={(ev) => {
                ev.preventDefault();
                const from = dragIndexRef.current;
                dragIndexRef.current = null;
                if (from == null) return;
                move(from, idx);
              }}
              className="flex items-center gap-2.5 px-2.5 py-2 border-b last:border-b-0"
              style={{ borderColor: "rgba(255,255,255,0.05)" }}
              data-testid={`rank-row-${idx}`}
            >
              <GripVertical className="w-4 h-4 text-white/40 shrink-0 cursor-grab active:cursor-grabbing" />
              <span className="w-6 text-[0.7rem] text-white/50 tabular text-right shrink-0">{idx + 1}.</span>
              <Input
                value={e.title}
                onChange={(ev) => rename(idx, ev.target.value)}
                onBlur={() => commitRename(idx)}
                className="flex-1 h-9 text-sm"
                data-testid={`rank-title-${idx}`}
              />
              <Select value={e.section} onValueChange={(v) => changeSection(idx, v)}>
                <SelectTrigger className="w-32 h-9 text-xs" data-testid={`rank-section-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SECTIONS.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <SectionBadge section={e.section} />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => remove(idx)}
                className="shrink-0"
                data-testid={`rank-remove-${idx}`}
                title="Remove"
              >
                <Trash2 className="w-3.5 h-3.5 text-red-400/80" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2 pt-1">
        <div className="flex-1">
          <div className="label-eyebrow mb-1">Add a rank</div>
          <Input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="e.g. Pastry Chef"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            className="h-10"
            data-testid="rank-new-title"
          />
        </div>
        <div className="w-32">
          <Select value={newSection} onValueChange={setNewSection}>
            <SelectTrigger className="h-10 text-xs" data-testid="rank-new-section">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SECTIONS.map((s) => (
                <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          onClick={add}
          className="h-10 shrink-0"
          disabled={saving || !newTitle.trim()}
          data-testid="rank-add"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}
